import React, { useState, useEffect } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getMyReports } from "../../api/myReports";
import { getEmployeeStories, getAllSprints, getMyRecentStoryComment } from "../../api/adminDashboard";
import { getAllTickets, getMyRecentTicketComment } from "../../api/ticketing";

// TODO: replace with real API calls once backend endpoints exist
// (self check-in, announcements, today's focus and recent activity
// have no controller yet — see dailyReporting.js / sprint.js / ticketing.js
// for the ones that already do: daily report %, sprint progress, tickets, leave)
const MOCK = {
  date: new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }),
  checkIn: { time: "9:42 AM", duration: "7h 18m", status: "Checked In" },
  sprint: { name: "Sprint 14", endsOn: "22 Aug", completed: 5, inProgress: 2, pending: 1, total: 8 },
  tickets: { New: 6, inProgress: 3, overdue: 1 },
  leave: { casual: 8, sick: 4, earned: 12 },
  announcements: [
    { title: "Ganesh Chaturthi Holiday Notice", postedAgo: "Posted 2 days ago" },
    { title: "Sprint 14 Review scheduled on 22 Aug", postedAgo: "Posted 4 days ago" },
  ],
  todaysFocus: [
    { title: "Ticket #GS-124", subtitle: "Login session timeout issue", tag: "Due today", color: "#ef4444", bg: "#fef2f2" },
    { title: "Sprint Story #46", subtitle: "Payment retry flow", tag: "Pending", color: "#d97706", bg: "#fffbeb" },
    { title: "Daily Report", subtitle: "Not submitted yet", tag: "Pending", color: "#d97706", bg: "#fffbeb" },
  ],
  recentActivity: [
    { text: "Updated Ticket #GS-124", when: "20 min ago" },
    { text: "Added 3 hrs to Daily Report", when: "1 hr ago" },
    { text: "Completed Sprint Story #42", when: "Yesterday, 5:40 PM" },
    { text: "Submitted leave request (21 Aug)", when: "Yesterday, 11:05 AM" },
  ],
};

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  height: "100%",
};

const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "12px" };
const bigNumberStyle = { fontSize: "24px", fontWeight: 700, color: "#111827" };
const subTextStyle = { fontSize: "12px", color: "#9ca3af" };

function Pill({ children, color, bg }) {
  return (
    <span style={{ fontSize: "11px", fontWeight: 600, color, background: bg, padding: "3px 10px", borderRadius: "999px" }}>
      {children}
    </span>
  );
}

function formatTimeAgo(dateInput) {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return `Yesterday, ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + ", " + date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
const activityStyles = {
  ticket: { icon: "🎫", color: "#2563eb" },
  comment: { icon: "💬", color: "#7c3aed" },
  story: { icon: "📌", color: "#059669" },
  default: { icon: "•", color: "#9ca3af" },
};

function renderActivityText(text) {
  return text.split(/(#[A-Za-z0-9-]+)/g).map((part, idx) =>
    part.startsWith("#") ? (
      <strong key={idx} style={{ color: "#111827" }}>{part}</strong>
    ) : (
      part
    )
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function EmployeeDashboard() {
  const navigate = useNavigate();
  const d = MOCK;
  const [employeeName] = useState(localStorage.getItem("userName") || "Employee");
  const [dailyReporting, setDailyReporting] = useState({
    percent: 0,
    reportedDays: 0,
    totalDays: 0,
    pendingDays: 0,
    todayStatus: "Pending",
  });
  const [sprintCard, setSprintCard] = useState({
    hasActiveSprint: false,
    name: "",
    endsOn: "",
    completed: 0,
    inProgress: 0,
    pending: 0,
    total: 0,
  });
  const [ticketCard, setTicketCard] = useState({
    New: 0,
    inProgress: 0,
    highPriority: 0,
  });
  const [focusTickets, setFocusTickets] = useState([]);
  const [todaysFocus, setTodaysFocus] = useState([]);
  const [recentTicketActivity, setRecentTicketActivity] = useState(null);
  const [recentStoryActivity, setRecentStoryActivity] = useState(null);
  const [recentTicketComment, setRecentTicketComment] = useState(null);
  const [recentStoryComment, setRecentStoryComment] = useState(null);
  const [recentActivity, setRecentActivity] = useState(d.recentActivity);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userInfoId = localStorage.getItem("id");
    if (!token || !userInfoId) return;

    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const toDate = formatLocalDate(today);
    const fromDate = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));

    // count weekdays (Mon–Sat) from the 1st of this month through today
    let totalDays = 0;
    const cursor = new Date(fromDate);
    const end = new Date(toDate);
    while (cursor <= end) {
      const day = cursor.getDay();
      if (day !== 0) totalDays++;
      cursor.setDate(cursor.getDate() + 1);
    }

    getMyReports(token, { userInfoId, fromDate, toDate })
      .then((res) => {
        const entries = res.data || [];
        const reportedDates = new Set(entries.map((e) => e.workingDate));
        const reportedDays = reportedDates.size;
        const percent = totalDays > 0 ? Math.round((reportedDays / totalDays) * 100) : 0;
        const pendingDays = Math.max(0, totalDays - reportedDays);
        const todayStatus = reportedDates.has(toDate) ? "Submitted" : "Pending";

        setDailyReporting({ percent, reportedDays, totalDays, pendingDays, todayStatus });
      })
      .catch((err) => console.error("Failed to load daily reporting stats:", err));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const loadSprintCard = async () => {
      try {
        const [sprintRes, storyRes] = await Promise.all([
          getAllSprints(token),
          getEmployeeStories(token),
        ]);

        const myUserId = Number(localStorage.getItem("id"));
        const myStories = storyRes.data || [];
        const getMyAssignedAt = (s) => {
          const su = (s.storyUsers || []).find((u) => Number(u.user?.id) === myUserId);
          return su?.assignedAt || null;
        };
        if (myStories.length > 0) {
          const latestStory = [...myStories].sort(
            (a, b) => new Date(getMyAssignedAt(b) || 0) - new Date(getMyAssignedAt(a) || 0)
          )[0];
          setRecentStoryActivity({
            text: `Assigned Sprint Story #${latestStory.id}${latestStory.storyTitle ? ": " + latestStory.storyTitle : ""}`,
            when: formatTimeAgo(getMyAssignedAt(latestStory)),
            rawDate: getMyAssignedAt(latestStory),
          });
        }

        const activeSprint = (sprintRes.data || []).find(
          (s) => s.sprintStatus === "ACTIVE"
        );

        if (!activeSprint) {
          setSprintCard((prev) => ({ ...prev, hasActiveSprint: false }));
          return;
        }

        const getStorySprintId = (s) => s.sprint?.id ?? s.sprintId ?? s.sprint;

        const sprintStories = (storyRes.data || []).filter(
          (s) => Number(getStorySprintId(s)) === Number(activeSprint.id)
        );
        const normalizeStatus = (s) => (s.status || "").toString().trim().toUpperCase();

        const completed = sprintStories.filter((s) => normalizeStatus(s) === "COMPLETED").length;
        const inProgress = sprintStories.filter((s) => {
          const st = normalizeStatus(s);
          return st === "INPROGRESS" || st === "INTESTING" || st === "IN_PROGRESS" || st === "IN_TESTING";
        }).length;
        const total = sprintStories.length;
        const pending = Math.max(0, total - completed - inProgress);

        const endsOn = activeSprint.endDate
          ? new Date(activeSprint.endDate).toLocaleDateString("en-US", { day: "numeric", month: "short" })
          : "—";

        setSprintCard({
          hasActiveSprint: true,
          name: activeSprint.sprintName,
          endsOn,
          completed,
          inProgress,
          pending,
          total,
        });

      } catch (err) {
        console.error("Failed to load current sprint stats:", err);
      }
    };

    loadSprintCard();
  }, []);
  useEffect(() => {
    const items = [...focusTickets];
    items.push({
      title: "Daily Report",
      subtitle: dailyReporting.todayStatus === "Submitted" ? "Submitted for today" : "Not submitted yet",
      tag: dailyReporting.todayStatus === "Submitted" ? "Done" : "Pending",
      color: dailyReporting.todayStatus === "Submitted" ? "#059669" : "#d97706",
      bg: dailyReporting.todayStatus === "Submitted" ? "#ecfdf5" : "#fffbeb",
    });
    setTodaysFocus(items);
  }, [focusTickets, dailyReporting]);

useEffect(() => {
    const items = [];
    if (recentTicketActivity) items.push({ ...recentTicketActivity, type: "ticket" });
    if (recentTicketComment) items.push({ ...recentTicketComment, type: "comment" });
    if (recentStoryActivity) items.push({ ...recentStoryActivity, type: "story" });
    if (recentStoryComment) items.push({ ...recentStoryComment, type: "comment" });

    // most recent actual timestamp first — ticket/story/comment sorted together by time,
    // not grouped by type
    items.sort((a, b) => new Date(b.rawDate || 0) - new Date(a.rawDate || 0));

    setRecentActivity(items.length > 0 ? items : d.recentActivity);
  }, [recentTicketActivity, recentStoryActivity, recentTicketComment, recentStoryComment]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const myUserId = Number(localStorage.getItem("id"));
    if (!token || !myUserId) return;

    const loadTicketCard = async () => {
      try {
        const res = await getAllTickets(token);
        const myTickets = (res.data || []).filter(
          (t) => Number(t.assigneeId) === myUserId
        );

        if (myTickets.length > 0) {
          const latestTicket = [...myTickets].sort(
            (a, b) => new Date(b.assignedAt || b.createdAt) - new Date(a.assignedAt || a.createdAt)
          )[0];
          setRecentTicketActivity({
            text: `New Ticket #${latestTicket.ticketNumber}: ${latestTicket.title}`,
            when: formatTimeAgo(latestTicket.assignedAt || latestTicket.createdAt),
            rawDate: latestTicket.assignedAt || latestTicket.createdAt,
          });
        }

        const New = myTickets.filter((t) => t.status === "New").length;
        const inProgress = myTickets.filter((t) => t.status === "In Progress").length;

        const highPriority = myTickets.filter((t) => {
          if (["Closed", "Resolved", "Rejected"].includes(t.status)) return false;
          return t.priority === "High";
        }).length;

        setTicketCard({ New, inProgress, highPriority });

                // surface every open ticket that's overdue, due today, or high priority
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const openTickets = myTickets.filter((t) => !["Closed", "Resolved", "Rejected"].includes(t.status));
        const isSameDay = (dateStr, ref) => {
          const d = new Date(dateStr);
          d.setHours(0, 0, 0, 0);
          return d.getTime() === ref.getTime();
        };
        const isOverdue = (t) => t.dueDate && new Date(t.dueDate).setHours(0, 0, 0, 0) < today.getTime();
        const isDueToday = (t) => t.dueDate && isSameDay(t.dueDate, today);
        const isHighPriority = (t) => t.priority === "High";

        const urgentTickets = openTickets.filter((t) => isOverdue(t) || isDueToday(t) || isHighPriority(t));

        setFocusTickets(
          urgentTickets.map((t) => ({
            title: `Ticket #${t.ticketNumber}`,
            subtitle: t.title,
            tag: isOverdue(t) ? "Overdue" : isDueToday(t) ? "Due today" : "High priority",
            color: "#ef4444",
            bg: "#fef2f2",
          }))
        );
      } catch (err) {
        console.error("Failed to load ticket stats:", err);
      }
    };

    loadTicketCard();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    getMyRecentTicketComment(token)
      .then((res) => {
        const c = res.data;
        if (c) {
          const snippet = (c.comment || "").trim();
          const truncated = snippet.length > 60 ? snippet.slice(0, 60) + "…" : snippet;
          setRecentTicketComment({
          text: `${c.commenterName} commented on Ticket #${c.ticketNumber}: "${truncated}"`,
            when: formatTimeAgo(c.createdAt),
            rawDate: c.createdAt,
          });
        }
      })
      .catch((err) => console.error("Failed to load recent ticket comment:", err));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    getMyRecentStoryComment(token)
      .then((res) => {
        const c = res.data;
        if (c) {
          const snippet = (c.comment || "").trim();
          const truncated = snippet.length > 60 ? snippet.slice(0, 60) + "…" : snippet;
          setRecentStoryComment({
            text: `${c.commenterName} commented on Sprint Story #${c.storyId}: "${truncated}"`,
            when: formatTimeAgo(c.createdAt),
            rawDate: c.createdAt,
          });
        }
      })
      .catch((err) => console.error("Failed to load recent story comment:", err));
  }, []);

  return (
    <div style={{ padding: "4px 8px 24px" }}>
      <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
{getGreeting()}, <span style={{ fontWeight: 400, fontStyle: "italic", color: "#6b7280" }}>{employeeName}</span>      </h5>
      <div style={{ fontSize: "13px", color: "#9ca3af", marginBottom: 20 }}>{d.date}</div>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card style={cardStyle} body>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={labelStyle}>Self Check-In</div>
              <Pill color="#059669" bg="#ecfdf5">{d.checkIn.status}</Pill>
            </div>
            <div style={bigNumberStyle}>{d.checkIn.duration}</div>
            <div style={subTextStyle}>Checked in at {d.checkIn.time} &middot; Working duration</div>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={cardStyle} body>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={labelStyle}>Daily Reporting</div>
              <Pill color="#d97706" bg="#fffbeb">Today's Report - {dailyReporting.todayStatus}</Pill>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div style={bigNumberStyle}>{dailyReporting.percent}%</div>
              <div style={subTextStyle}>{dailyReporting.reportedDays} of {dailyReporting.totalDays} days reported</div>
            </div>
            <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, margin: "8px 0" }}>
              <div style={{ height: 6, width: `${dailyReporting.percent}%`, background: "#2E5CB8", borderRadius: 999 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "12px", color: "#d97706" }}>Pending: {dailyReporting.pendingDays} days</span>
              <button
                onClick={() => navigate("/employeereporting/dailyreporting")}
                style={{ fontSize: "12px", fontWeight: 600, color: "#fff", background: "#2E5CB8", border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}
              >
                Add Today's Report
              </button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card style={cardStyle} body>
            {sprintCard.hasActiveSprint ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={labelStyle}>Current Sprint</div>
                  <span style={subTextStyle}>Sprint ends: {sprintCard.endsOn}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 600 }}>
                  <span>{sprintCard.name}</span>
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    {sprintCard.completed} of {sprintCard.total} stories completed
                  </span>
                </div>
                <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, margin: "8px 0" }}>
                  <div
                    style={{
                      height: 6,
                      width: `${sprintCard.total > 0 ? (sprintCard.completed / sprintCard.total) * 100 : 0}%`,
                      background: "#22c55e",
                      borderRadius: 999,
                    }}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#6b7280" }}>
                  <span>{sprintCard.completed} Completed</span>
                  <span>{sprintCard.inProgress} In Progress</span>
                  <span>{sprintCard.pending} Pending</span>
                </div>
              </>
            ) : (
              <>
                <div style={labelStyle}>Current Sprint</div>
                <div style={{ ...subTextStyle, padding: "12px 0" }}>No active sprint right now.</div>
              </>
            )}
          </Card>
        </Col>
        <Col md={6}>
          <Card style={cardStyle} body>
            <div style={labelStyle}>My Tickets</div>
            <Row className="text-center mb-2">
              <Col><div style={bigNumberStyle}>{ticketCard.New}</div><div style={subTextStyle}>New</div></Col>
              <Col><div style={bigNumberStyle}>{ticketCard.inProgress}</div><div style={subTextStyle}>In Progress</div></Col>
              <Col><div style={bigNumberStyle}>{ticketCard.highPriority}</div><div style={subTextStyle}>High Priority</div></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card style={cardStyle} body>
            <div style={labelStyle}>Leave Status</div>
            <Row className="text-center">
              <Col><div style={bigNumberStyle}>{d.leave.casual}</div><div style={subTextStyle}>Casual</div></Col>
              <Col><div style={bigNumberStyle}>{d.leave.sick}</div><div style={subTextStyle}>Sick</div></Col>
              <Col><div style={bigNumberStyle}>{d.leave.earned}</div><div style={subTextStyle}>Earned</div></Col>
            </Row>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={cardStyle} body>
            <div style={labelStyle}>Announcements</div>
            {d.announcements.map((a) => (
              <div key={a.title} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "13px", fontWeight: 500 }}>{a.title}</div>
                <div style={subTextStyle}>{a.postedAgo}</div>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      <Card style={{ ...cardStyle, marginBottom: 12 }} body>
        <div style={labelStyle}>Today's Focus</div>
        {todaysFocus.length === 0 && (
          <div style={{ ...subTextStyle, padding: "12px 0" }}>Nothing on your focus list right now.</div>
        )}
        {todaysFocus.map((item, i) => (
          <div
            key={item.title}
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
            }}
          >
            <div>
              <div style={{ fontSize: "13px", fontWeight: 500 }}>{item.title}</div>
              <div style={subTextStyle}>{item.subtitle}</div>
            </div>
            <Pill color={item.color} bg={item.bg}>{item.tag}</Pill>
          </div>
        ))}
      </Card>

      <Card style={cardStyle} body>
        <div style={labelStyle}>Recent Activity</div>
        {recentActivity.map((item, i) => {
          const s = activityStyles[item.type] || activityStyles.default;
          return (
            <div
              key={item.text}
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                listStyle: "none",
              }}
            >
              <span style={{ fontSize: "13px", color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "14px", flexShrink: 0 }}>{s.icon}</span>
                <span>{renderActivityText(item.text)}</span>
              </span>
              <span style={subTextStyle}>{item.when}</span>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default EmployeeDashboard;